import { Link } from "react-router-dom";
import StudyGuideShell from "../courses/StudyGuideShell";
import "../multivariableCalculus/PartialDerivativesGuide.css";
import { GuideMcqSection } from "../../components/GuideMcq";
import { useProgress } from "../../context/ProgressContext";
import { TheoryBox, ProcedureBox, CertificateExample } from "./CalcBlocks";
import { CALC_C_ORTHOGONAL_POLY_QUIZ } from "../../data/calcAgSpecialQuizzes";

function Divider() {
  return <hr className="divider" />;
}

function CurriculumBadge({ code }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        padding: "0.4rem 0.85rem",
        borderRadius: "999px",
        background: "rgba(13, 148, 136, 0.12)",
        border: "1px solid rgba(13, 148, 136, 0.3)",
        color: "var(--teal)",
        fontSize: "0.85rem",
        fontWeight: 600,
        marginBottom: "1.25rem",
      }}
    >
      <span>📚 Curriculum Ref:</span>
      <span>{code}</span>
    </div>
  );
}

export default function OrthogonalPolyGuide({ part = 1 }) {
  const { saveQuizScore } = useProgress();

  if (part === 2) {
    return (
      <StudyGuideShell guideClass="partial-derivatives-guide" title="Orthogonal Polynomials: Legendre, Chebyshev & Hermite Families (Part 2)">
        <nav className="sidebar">
          <div className="sb-brand"><div className="sb-title">Orthogonal Poly · Part 2</div></div>
          <a className="sb-link" href="#legendre-family">Legendre Polynomials Pₙ(x)</a>
          <a className="sb-link" href="#chebyshev-family">Chebyshev Polynomials Tₙ(x)</a>
          <a className="sb-link" href="#hermite-family">Hermite Polynomials Hₙ(x)</a>
          <a className="sb-link" href="#poly-comparison">Comparative Tri-Family Matrix</a>
          <a className="sb-link" href="#poly-worked-examples">Certificate Examples</a>
          <a className="sb-link" href="#quiz-orthogonal-poly-checkpoint">Interactive Quiz · 20 Qs</a>
          <Link className="sb-link" to="/courses/calculus-analytical-geometry">All modules</Link>
        </nav>
        <main className="main">
          <header className="ch-hdr">
            <div className="ch-eye">Module C: PDEs &amp; Polynomials · Part 2 of 2</div>
            <h1 className="ch-title">The Classical Orthogonal Families: Legendre, Chebyshev &amp; Hermite</h1>
            <p className="ch-sub">Rodrigues formulas, three-term recurrence systems, Chebyshev minimax nodes, and quantum oscillator eigenfunctions</p>
            <span className="ch-orn">✦ &nbsp; ✦ &nbsp; ✦</span>
          </header>

          <CurriculumBadge code="Math-203 / PDEs & Polynomials · Module C (Part 2)" />

          <div className="opening-note-box">
            <p className="opening-note">
              <strong>Operational Blueprint:</strong>{" "}
              {"In this section, we master the three fundamental classical orthogonal systems: Legendre polynomials on $[-1, 1]$ (governing spherical potentials), Chebyshev polynomials (optimizing polynomial interpolation), and Hermite polynomials (forming the quantum harmonic oscillator spectrum)."}
            </p>
          </div>
          <Divider />

          <section className="section" id="legendre-family">
            <div className="sec-badge">Section 2.1</div>
            <h2 className="sec-title">Legendre Polynomials $P_n(x)$</h2>
            <TheoryBox title="Uniform Weight on [-1, 1]">
              <p>
                {"Orthogonal on $[-1, 1]$ with uniform weight $w(x) = 1$. Generated by Rodrigues' formula:"}
              </p>
              <p>
                {"$$P_n(x) = \\frac{1}{2^n n!} \\frac{d^n}{dx^n} \\left(x^2 - 1\\right)^n$$"}
              </p>
              <ul>
                <li>{"$P_0(x) = 1$"}</li>
                <li>{"$P_1(x) = x$"}</li>
                <li>{"$P_2(x) = \\frac{1}{2}(3x^2 - 1)$"}</li>
                <li>{"$P_3(x) = \\frac{1}{2}(5x^3 - 3x)$"}</li>
              </ul>
              <p>
                {"**Bonnet's Recurrence:** $(n + 1)P_{n+1}(x) = (2n + 1)x P_n(x) - n P_{n-1}(x)$."}
              </p>
              <p>
                {"**Normalization:** $\\int_{-1}^1 [P_n(x)]^2\\,dx = \\frac{2}{2n + 1}$."}
              </p>
              <p>
                {"**ODE:** $(1 - x^2) y'' - 2x y' + n(n + 1) y = 0$."}
              </p>
            </TheoryBox>
          </section>

          <section className="section" id="chebyshev-family">
            <div className="sec-badge">Section 2.2</div>
            <h2 className="sec-title">Chebyshev Polynomials $T_n(x)$ (First Kind)</h2>
            <TheoryBox title="Trigonometric Projection & Minimax Optimization">
              <p>
                {"Orthogonal on $[-1, 1]$ with singular weight $w(x) = (1 - x^2)^{-1/2}$. Defined by the exact trigonometric identity:"}
              </p>
              <p>
                {"$$T_n(x) = \\cos(n \\arccos x) = \\cos(n\\theta) \\quad (x = \\cos\\theta)$$"}
              </p>
              <ul>
                <li>{"$T_0(x) = 1$"}</li>
                <li>{"$T_1(x) = x$"}</li>
                <li>{"$T_2(x) = 2x^2 - 1$"}</li>
                <li>{"$T_3(x) = 4x^3 - 3x$"}</li>
                <li>{"$T_4(x) = 8x^4 - 8x^2 + 1$"}</li>
              </ul>
              <p>
                {"**Recurrence:** $T_{n+1}(x) = 2x T_n(x) - T_{n-1}(x)$."}
              </p>
              <p>
                {"**Chebyshev Nodes (Roots):** $x_k = \\cos\\left(\\frac{2k - 1}{2n}\\pi\\right)$ for $k = 1, \\dots, n$. Interpolating at these nodes completely eliminates Runge's edge-oscillation phenomenon."}
              </p>
            </TheoryBox>
          </section>

          <section className="section" id="hermite-family">
            <div className="sec-badge">Section 2.3</div>
            <h2 className="sec-title">Hermite Polynomials $H_n(x)$ (Physicist's)</h2>
            <TheoryBox title="Infinite Domain with Gaussian Weight">
              <p>
                {"Orthogonal on $(-\\infty, \\infty)$ with Gaussian weight $w(x) = e^{-x^2}$. Rodrigues' formula:"}
              </p>
              <p>
                {"$$H_n(x) = (-1)^n e^{x^2} \\frac{d^n}{dx^n} \\left(e^{-x^2}\\right)$$"}
              </p>
              <ul>
                <li>{"$H_0(x) = 1$"}</li>
                <li>{"$H_1(x) = 2x$"}</li>
                <li>{"$H_2(x) = 4x^2 - 2$"}</li>
                <li>{"$H_3(x) = 8x^3 - 12x$"}</li>
              </ul>
              <p>
                {"**Recurrence:** $H_{n+1}(x) = 2x H_n(x) - 2n H_{n-1}(x)$."}
              </p>
              <p>
                {"**Normalization:** $\\int_{-\\infty}^\\infty [H_n(x)]^2 e^{-x^2}\\,dx = \\sqrt{\\pi}\\,2^n n!$."}
              </p>
              <p>
                {"**Quantum Application:** Stationary states of the 1D quantum harmonic oscillator are $\\psi_n(x) = \\frac{1}{\\sqrt{2^n n! \\sqrt{\\pi}}} e^{-x^2/2} H_n(x)$."}
              </p>
            </TheoryBox>
          </section>

          <section className="section" id="poly-comparison">
            <div className="sec-badge">Section 2.4</div>
            <h2 className="sec-title">Comparative Tri-Family Matrix</h2>
            <ProcedureBox title="Summary of Canonical Orthogonal Polynomials">
              <ol>
                <li>
                  <strong>Legendre:</strong> {"Domain $[-1, 1]$, $w(x) = 1$, Norm $h_n = \\frac{2}{2n+1}$, Governing Physics: Spherical Harmonics & Gravitational Potentials."}
                </li>
                <li>
                  <strong>Chebyshev:</strong> {"Domain $[-1, 1]$, $w(x) = (1 - x^2)^{-1/2}$, Norm $h_n = \\pi/2$ ($n \\ge 1$), Governing Physics: Numerical Approximation & Filter Design."}
                </li>
                <li>
                  <strong>Hermite:</strong> {"Domain $(-\\infty, \\infty)$, $w(x) = e^{-x^2}$, Norm $h_n = \\sqrt{\\pi} 2^n n!$, Governing Physics: Quantum Harmonic Oscillator & Normal Probability."}
                </li>
              </ol>
            </ProcedureBox>
          </section>

          <section className="section" id="poly-worked-examples">
            <h2 className="sec-title">Certificate Worked Examples</h2>
            <CertificateExample
              number={1}
              tier="Hard"
              title="Fourier-Legendre Expansion of Quadratic Function"
              setup="Express $f(x) = 5x^2 - 3x + 4$ on $[-1, 1]$ as an exact linear combination of Legendre polynomials $P_0(x), P_1(x), P_2(x)$."
              steps={[
                "Recall the lower-order Legendre polynomials: $P_0(x) = 1, \\, P_1(x) = x, \\, P_2(x) = \\frac{1}{2}(3x^2 - 1)$.",
                "Express monic powers in terms of Legendre polynomials:",
                "$x = P_1(x)$.",
                "$P_2(x) = \\frac{3}{2}x^2 - \\frac{1}{2} \\implies \\frac{3}{2}x^2 = P_2(x) + \\frac{1}{2}P_0(x) \\implies x^2 = \\frac{2}{3}P_2(x) + \\frac{1}{3}P_0(x)$.",
                "Substitute powers into $f(x) = 5x^2 - 3x + 4$:",
                "$f(x) = 5\\left[\\frac{2}{3}P_2(x) + \\frac{1}{3}P_0(x)\\right] - 3 P_1(x) + 4 P_0(x)$.",
                "Expand and collect coefficients for each Legendre basis:",
                "$P_2 \\text{ term}: \\frac{10}{3} P_2(x)$.",
                "$P_1 \\text{ term}: -3 P_1(x)$.",
                "$P_0 \\text{ term}: \\frac{5}{3} + 4 = \\frac{5 + 12}{3} = \\frac{17}{3} P_0(x)$.",
                "Assemble the final decomposition: $f(x) = \\frac{17}{3}P_0(x) - 3P_1(x) + \\frac{10}{3}P_2(x)$."
              ]}
              result="f(x) = \frac{17}{3}P_0(x) - 3P_1(x) + \frac{10}{3}P_2(x)"
              check="Check at $x = 1$: $f(1) = 5 - 3 + 4 = 6$. Formula: $\frac{17}{3}(1) - 3(1) + \frac{10}{3}(1) = \frac{27}{3} - 3 = 9 - 3 = 6$. Exact match!"
            />
          </section>

          <GuideMcqSection
            id="quiz-orthogonal-poly-checkpoint"
            badge="Topic checkpoint · 20 questions"
            title="Orthogonal Polynomials Checkpoint"
            scoreId="score-orthogonal-poly-checkpoint"
            section="orthogonal-poly-checkpoint"
            questions={CALC_C_ORTHOGONAL_POLY_QUIZ}
            onComplete={(score, total) => saveQuizScore("guide-mcq-orthogonal-poly-checkpoint", score, total)}
          />
        </main>
      </StudyGuideShell>
    );
  }

  // Part 1
  return (
    <StudyGuideShell guideClass="partial-derivatives-guide" title="Orthogonal Polynomials: Weighted Inner Products & Recurrences (Part 1)">
      <nav className="sidebar">
        <div className="sb-brand"><div className="sb-title">Orthogonal Poly · Part 1</div></div>
        <a className="sb-link" href="#inner-product">Weighted Hilbert Spaces</a>
        <a className="sb-link" href="#gram-schmidt">Gram-Schmidt Generation</a>
        <a className="sb-link" href="#three-term-recurrence">Three-Term Recurrence</a>
        <a className="sb-link" href="#rodrigues-general">Generalized Rodrigues Formula</a>
        <a className="sb-link" href="#poly-p1-examples">Certificate Examples</a>
        <Link className="sb-link" to="/courses/calculus-analytical-geometry">All modules</Link>
      </nav>
      <main className="main">
        <header className="ch-hdr">
          <div className="ch-eye">Module C: PDEs &amp; Polynomials · Part 1 of 2</div>
          <h1 className="ch-title">Orthogonal Polynomial Foundations &amp; Recurrences</h1>
          <p className="ch-sub">Weighted function spaces, Gram-Schmidt construction, generalized three-term relations, and zero-interlacing theorems</p>
          <span className="ch-orn">✦ &nbsp; ✦ &nbsp; ✦</span>
        </header>

        <CurriculumBadge code="Math-203 / PDEs & Polynomials · Module C (Part 1)" />

        <div className="opening-note-box">
          <p className="opening-note">
            <strong>Operational Blueprint:</strong>{" "}
            {"We generalize linear algebraic orthogonality into functional analysis over function spaces $L^2_w(a, b)$. By introducing a non-negative weight function $w(x)$, we construct complete orthogonal polynomial bases that diagonalize differential operators and minimize truncation errors."}
          </p>
        </div>
        <Divider />

        <section className="section" id="inner-product">
          <div className="sec-badge">Section 1.1</div>
          <h2 className="sec-title">Weighted Inner Product Spaces</h2>
          <TheoryBox title="The L²_w(a, b) Hilbert Space">
            <p>
              {"Given an interval $(a, b)$ and a continuous strictly positive weight function $w(x) > 0$ with finite moments $\\mu_k = \\int_a^b x^k w(x)\\,dx < \\infty$, the weighted inner product is:"}
            </p>
            <p>
              {"$$\\langle f, g \\rangle_w = \\int_a^b f(x) g(x) w(x)\\,dx$$"}
            </p>
            <p>
              {"A sequence of polynomials $\\{p_n(x)\\}_{n=0}^\\infty$ with $\\deg(p_n) = n$ is orthogonal if:"}
            </p>
            <p>
              {"$$\\langle p_n, p_m \\rangle_w = \\int_a^b p_n(x) p_m(x) w(x)\\,dx = h_n \\delta_{nm}$$"}
            </p>
          </TheoryBox>
        </section>

        <section className="section" id="gram-schmidt">
          <div className="sec-badge">Section 1.2</div>
          <h2 className="sec-title">Gram-Schmidt Orthogonalization Process</h2>
          <TheoryBox title="Constructive Basis Generation">
            <p>
              {"Starting from the standard monomial basis $\\{1, x, x^2, x^3, \\dots\\}$, Gram-Schmidt produces orthogonal polynomials recursively:"}
            </p>
            <p>
              {"$$p_0(x) = 1, \\quad p_n(x) = x^n - \\sum_{k=0}^{n-1} \\frac{\\langle x^n, p_k \\rangle_w}{\\|p_k\\|_w^2} p_k(x)$$"}
            </p>
            <p>
              {"**Zero-Interlacing Theorem:** Every orthogonal polynomial $p_n(x)$ has exactly $n$ distinct real roots in the interior open interval $(a, b)$, and between any two roots of $p_{n+1}(x)$ lies exactly one root of $p_n(x)$."}
            </p>
          </TheoryBox>
        </section>

        <section className="section" id="three-term-recurrence">
          <div className="sec-badge">Section 1.3</div>
          <h2 className="sec-title">The Fundamental Three-Term Recurrence Relation</h2>
          <TheoryBox title="Favard's Theorem & Efficient Evaluation">
            <p>
              {"Every family of orthogonal polynomials satisfies a universal three-term recurrence relation:"}
            </p>
            <p>
              {"$$p_{n+1}(x) = (A_n x + B_n) p_n(x) - C_n p_{n-1}(x) \\quad (n \\ge 1)$$"}
            </p>
            <p>
              {"with $A_n > 0$ and $C_n > 0$. This guarantees $O(n)$ numerical stability when evaluating high-degree polynomials, bypassing costly powers $x^n$."}
            </p>
          </TheoryBox>
        </section>

        <section className="section" id="rodrigues-general">
          <div className="sec-badge">Section 1.4</div>
          <h2 className="sec-title">The Generalized Rodrigues Formula</h2>
          <TheoryBox title="Differential Operator Representation">
            <p>
              {"Classical orthogonal polynomials (Legendre, Chebyshev, Hermite, Laguerre) are uniquely characterized by the Rodrigues differential representation:"}
            </p>
            <p>
              {"$$p_n(x) = \\frac{1}{c_n w(x)} \\frac{d^n}{dx^n} \\left[ w(x) \\sigma(x)^n \\right]$$"}
            </p>
            <p>
              {"where $\\sigma(x)$ is a polynomial in $x$ of degree at most 2, and $c_n$ is a normalization constant."}
            </p>
          </TheoryBox>
        </section>

        <section className="section" id="poly-p1-examples">
          <h2 className="sec-title">Worked Examples</h2>
          <CertificateExample
            number={1}
            tier="Medium"
            title="Gram-Schmidt Construction of P₂(x) on [-1, 1]"
            setup="Construct the monic second-degree orthogonal polynomial $q_2(x)$ on $[-1, 1]$ with weight $w(x) = 1$ using Gram-Schmidt orthogonalization from $x^2$."
            steps={[
              "Identify previous monic orthogonal polynomials: $q_0(x) = 1$ and $q_1(x) = x$.",
              "Compute norm squared of $q_0$: $\\|q_0\\|^2 = \\int_{-1}^1 1^2\\,dx = 2$.",
              "Compute norm squared of $q_1$: $\\|q_1\\|^2 = \\int_{-1}^1 x^2\\,dx = \\left[ \\frac{x^3}{3} \\right]_{-1}^1 = \\frac{2}{3}$.",
              "Compute projections of $x^2$: $\\langle x^2, q_0 \\rangle = \\int_{-1}^1 x^2 (1)\\,dx = 2/3$.",
              "Compute projection onto $q_1$: $\\langle x^2, q_1 \\rangle = \\int_{-1}^1 x^3\\,dx = 0$ (odd integrand on symmetric interval).",
              "Apply Gram-Schmidt formula: $q_2(x) = x^2 - \\frac{\\langle x^2, q_0 \\rangle}{\\|q_0\\|^2} q_0(x) - \\frac{\\langle x^2, q_1 \\rangle}{\\|q_1\\|^2} q_1(x) = x^2 - \\frac{2/3}{2} (1) - 0 = x^2 - \\frac{1}{3}$.",
              "Normalize to standard Legendre convention where $P_n(1) = 1$: Since $q_2(1) = 1 - 1/3 = 2/3$, multiply by $3/2$: $P_2(x) = \\frac{3}{2}(x^2 - 1/3) = \\frac{1}{2}(3x^2 - 1)$."
            ]}
            result="q_2(x) = x^2 - \frac{1}{3} \implies P_2(x) = \frac{1}{2}(3x^2 - 1)"
            check="Orthogonality check: $\int_{-1}^1 P_2(x) P_0(x)\,dx = \frac{1}{2}\int_{-1}^1 (3x^2 - 1)\,dx = \frac{1}{2}[x^3 - x]_{-1}^1 = 0$. Confirmed."
          />
        </section>

        <section className="section">
          <h2 className="sec-title">Continue to Part 2</h2>
          <p>
            Advance to Section 2 for full analysis of the Legendre, Chebyshev, and Hermite polynomial families, comparative properties, certificate worked examples, and the 20-question checkpoint quiz.
          </p>
          <Link className="primary-action" to="/orthogonal-polynomials/2" style={{ display: "inline-block", marginTop: "1rem" }}>
            Proceed to Section 2 →
          </Link>
        </section>
      </main>
    </StudyGuideShell>
  );
}
